import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react'
import { useFinePointer, usePrefersReducedMotion } from '@/hooks/use-media-query'

type CursorMode = 'default' | 'link' | 'media' | 'text' | 'label'

const RING_SIZE: Record<CursorMode, number> = {
    default: 34,
    link: 58,
    media: 82,
    text: 12,
    label: 86
}

const TEXT_FIELDS = 'input:not([type=checkbox]):not([type=radio]):not([type=submit]):not([type=button]), textarea, [contenteditable=true]'
const INTERACTIVE = 'a, button, [role=button], select, label, summary, [data-cursor-link]'
const MEDIA = 'img, video, picture, [data-cursor-media]'

/**
 * Small dot plus a soft gold ring. The ring grows over links and images and
 * shows a word for any element (or ancestor) carrying `data-cursor="View"`.
 * Only mounts for mouse users; touch devices keep their normal behaviour.
 */
export const Cursor: FC = (): ReactNode => {
    const finePointer = useFinePointer()

    if (!finePointer) return null

    return <CursorInner />
}

const CursorInner: FC = (): ReactNode => {
    const reducedMotion = usePrefersReducedMotion()
    const x = useMotionValue(-200)
    const y = useMotionValue(-200)
    const spring = reducedMotion ? { stiffness: 2000, damping: 100 } : { stiffness: 420, damping: 34, mass: 0.55 }
    const ringX = useSpring(x, spring)
    const ringY = useSpring(y, spring)
    const [mode, setMode] = useState<CursorMode>('default')
    const [label, setLabel] = useState('')
    const [visible, setVisible] = useState(false)
    const [pressed, setPressed] = useState(false)

    useEffect(() => {
        const root = document.documentElement
        let lastX = -200
        let lastY = -200
        let frame = 0

        const evaluate = (target: Element | null) => {
            if (!target) return

            const labelled = target.closest<HTMLElement>('[data-cursor]')

            if (labelled?.dataset.cursor) {
                setMode('label')
                setLabel(labelled.dataset.cursor)
                return
            }

            if (target.closest(TEXT_FIELDS)) setMode('text')
            else if (target.closest(INTERACTIVE)) setMode('link')
            else if (target.closest(MEDIA)) setMode('media')
            else setMode('default')
        }

        const handleMove = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return

            lastX = event.clientX
            lastY = event.clientY
            x.set(lastX)
            y.set(lastY)
            setVisible(true)
        }

        const handleOver = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return
            if (event.target instanceof Element) evaluate(event.target)
        }

        const handleScroll = () => {
            if (frame) return

            frame = requestAnimationFrame(() => {
                frame = 0
                evaluate(document.elementFromPoint(lastX, lastY))
            })
        }

        const handleDown = () => setPressed(true)
        const handleUp = () => setPressed(false)
        const handleLeave = () => setVisible(false)

        root.classList.add('has-custom-cursor')
        window.addEventListener('pointermove', handleMove, { passive: true })
        window.addEventListener('scroll', handleScroll, { passive: true })
        document.addEventListener('pointerover', handleOver)
        window.addEventListener('pointerdown', handleDown)
        window.addEventListener('pointerup', handleUp)
        root.addEventListener('mouseleave', handleLeave)

        return () => {
            cancelAnimationFrame(frame)
            root.classList.remove('has-custom-cursor')
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('scroll', handleScroll)
            document.removeEventListener('pointerover', handleOver)
            window.removeEventListener('pointerdown', handleDown)
            window.removeEventListener('pointerup', handleUp)
            root.removeEventListener('mouseleave', handleLeave)
        }
    }, [x, y])

    const size = RING_SIZE[mode]
    const isLabel = mode === 'label'

    return (
        <div
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[110] transition-opacity duration-300'
            style={{ opacity: visible ? 1 : 0 }}
        >
            <motion.div className='absolute left-0 top-0' style={{ x: ringX, y: ringY }}>
                <motion.div
                    className='flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border'
                    initial={false}
                    animate={{
                        width: size,
                        height: size,
                        scale: pressed ? 0.86 : 1,
                        backgroundColor: isLabel ? 'rgba(214, 168, 92, 0.96)' : mode === 'link' ? 'rgba(214, 168, 92, 0.08)' : 'rgba(214, 168, 92, 0)',
                        borderColor: isLabel ? 'rgba(214, 168, 92, 0)' : 'rgba(214, 168, 92, 0.6)'
                    }}
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                >
                    <AnimatePresence mode='wait'>
                        {isLabel && (
                            <motion.span
                                key={label}
                                className='text-[11px] font-medium uppercase tracking-[0.2em] text-ink'
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.6 }}
                                transition={{ duration: 0.2 }}
                            >
                                {label}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>

            <motion.div className='absolute left-0 top-0' style={{ x, y }}>
                <motion.div
                    className='size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground'
                    initial={false}
                    animate={{ opacity: isLabel || mode === 'text' ? 0 : 1, scale: mode === 'link' ? 0.6 : 1 }}
                    transition={{ duration: 0.2 }}
                />
            </motion.div>
        </div>
    )
}
